import type { HTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * `panel` bruges til større, sammenhængende områder (grafer, lister),
   * `card` til enkeltstående kort. Forskellen er radius og fladens vægt.
   */
  as?: 'card' | 'panel'
  /** Løft ved hover. Kun til kort der kan klikkes. */
  interactive?: boolean
}

export function Card({
  as = 'card',
  interactive = false,
  className,
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        as === 'card' ? 'surface-card' : 'surface-panel',
        interactive && [
          'cursor-pointer transition-[border-color,background-color,transform]',
          'duration-[var(--duration-hover)] ease-[var(--ease-out-soft)]',
          'hover:border-line-strong hover:bg-overlay-soft',
          'focus-within:border-brand-500/50',
        ],
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function CardHeader({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-start justify-between gap-3 px-5 pt-4 pb-3', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Overskriftsniveau. Skal vælges så sidens heading-hierarki forbliver korrekt. */
  level?: 2 | 3 | 4
  description?: ReactNode
}

export function CardTitle({
  level = 3,
  description,
  className,
  children,
  ...props
}: CardTitleProps) {
  const Heading = `h${level}` as const

  return (
    <div className="min-w-0">
      <Heading className={cn('text-[15px] leading-tight font-semibold', className)} {...props}>
        {children}
      </Heading>
      {description && <p className="mt-0.5 text-xs text-text-muted">{description}</p>}
    </div>
  )
}

export function CardContent({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-5 pb-5', className)} {...props}>
      {children}
    </div>
  )
}

export function CardFooter({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex items-center gap-2 border-t border-line-subtle px-5 py-3', className)}
      {...props}
    >
      {children}
    </div>
  )
}
